import { noteNameSchema, type NoteName } from '@tunasong/schemas'
import { ALL_CHORDS } from './all-chords.js'
import { ALL_CHORD_TYPES } from './chord-types.js'
import { getName } from './get-name.js'
import { fromName } from './library.js'

/** One tone of a chord: its degree above the root (1–7, so a 9th is 2) and its spelling */
export interface ChordTone {
  degree: number
  name: NoteName
  /** 0–11 */
  pitchClass: number
  /**
   * Set when the letter-correct spelling is one `NoteName` cannot hold, a double sharp or a rare double
   * flat, so the name is its enharmonic: from the sharp side for a sharp, from the flat side for a flat
   * (C## → D, Cbb → Bb). The degree and pitch class are the chord's own (GH #2454, Bowie).
   */
  respelled?: true
}

const letters = ['C', 'D', 'E', 'F', 'G', 'A', 'B'] as const
type Letter = (typeof letters)[number]
const naturalPitch: Record<Letter, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 }
const romanDegrees = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII'] as const
const majorScaleSemitones = [0, 2, 4, 5, 7, 9, 11]
const accidentalSemitones: Record<string, number> = { '': 0, b: -1, bb: -2, '#': 1, '##': 2 }
const intervalPattern = /^(bb|b|##|#)?(VII|VI|IV|V|III|II|I)$/
const sharpNames: NoteName[] = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B']
const flatNames: NoteName[] = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'Gb', 'G', 'Ab', 'A', 'Bb', 'B']

const mod12 = (n: number) => ((n % 12) + 12) % 12

function isLetter(value: string): value is Letter {
  return (letters as readonly string[]).includes(value)
}

function isNoteName(name: string): name is NoteName {
  return noteNameSchema.safeParse(name).success
}

/** An interval spec such as `bVII` as a degree and semitones above the root */
const parseInterval = (interval: string) => {
  const match = intervalPattern.exec(interval)
  if (!match) {
    return undefined
  }
  const index = romanDegrees.indexOf(match[2] as (typeof romanDegrees)[number])
  return { degree: index + 1, semitones: majorScaleSemitones[index] + accidentalSemitones[match[1] ?? ''] }
}

const pitchClassOf = (note: NoteName) => {
  const letter = note[0]
  const accidental = accidentalSemitones[note.slice(1)]
  return isLetter(letter) && accidental !== undefined ? mod12(naturalPitch[letter] + accidental) : undefined
}

/**
 * Spell the note `semitones` above `root` on the letter `degree - 1` steps up from the root's letter. When
 * that spelling is not a `NoteName`, its enharmonic from the same side: sharps for sharps, flats for flats.
 */
const spell = (
  root: NoteName,
  degree: number,
  semitones: number
): Pick<ChordTone, 'name' | 'respelled'> | undefined => {
  const rootLetter = root[0]
  const rootPitch = pitchClassOf(root)
  if (!isLetter(rootLetter) || rootPitch === undefined) {
    return undefined
  }
  const pitch = mod12(rootPitch + semitones)
  const letter = letters[(letters.indexOf(rootLetter) + degree - 1) % 7]
  const offset = ((pitch - naturalPitch[letter] + 18) % 12) - 6
  const accidental = Object.entries(accidentalSemitones).find(([, value]) => value === offset)?.[0]
  const name = accidental === undefined ? undefined : `${letter}${accidental}`
  if (name !== undefined && isNoteName(name)) {
    return { name }
  }
  return { name: (offset > 0 ? sharpNames : flatNames)[pitch], respelled: true }
}

/**
 * The tones of a chord symbol, from `ChordLib`'s interval specs, each spelled from the symbol's own root
 * so that Ab7's 7th is Gb (Pitch Trainer Phase 2, plan M2 step 2.4). A slash bass is not a tone.
 * Undefined for N.C. and for symbols the library does not know.
 */
export const chordTones = (symbol: string): ChordTone[] | undefined => {
  const chord = fromName(symbol)
  const library = chord ? ALL_CHORDS[getName({ root: chord.root, variant: chord.variant })] : undefined
  const rootPitch = chord ? pitchClassOf(chord.root) : undefined
  if (!chord || !library || rootPitch === undefined) {
    return undefined
  }
  return library.builder.elementNames
    .flatMap(element => ALL_CHORD_TYPES[element]?.intervals ?? [])
    .flatMap(interval => {
      const parsed = parseInterval(interval)
      const spelled = parsed && spell(chord.root, parsed.degree, parsed.semitones)
      return parsed && spelled
        ? [{ degree: parsed.degree, ...spelled, pitchClass: mod12(rootPitch + parsed.semitones) }]
        : []
    })
}
